/**
 * GoogleAuthCallback — synchronously detects `#session_id=` in the URL hash
 * (set by Emergent-managed Google OAuth redirect back to /account) and
 * exchanges it for our native customer JWT via POST /api/customers/google/session.
 *
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS,
 * THIS BREAKS THE AUTH.
 *
 * Rendered as a full-screen loader while the exchange is in flight.
 */
import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Loader2, AlertCircle } from 'lucide-react';
import { useCustomerAuth } from '../contexts/CustomerAuthContext';

export default function GoogleAuthCallback({ onDone }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { exchangeGoogleSession } = useCustomerAuth();
  const [err, setErr] = useState('');
  const processedRef = useRef(false);

  useEffect(() => {
    // Guard against React StrictMode double-invoke and re-renders.
    if (processedRef.current) return;
    const hash = location.hash || '';
    const match = hash.match(/session_id=([^&]+)/);
    if (!match) return;
    processedRef.current = true;
    const sessionId = decodeURIComponent(match[1]);
    (async () => {
      try {
        const result = await exchangeGoogleSession(sessionId);
        // Strip the session_id from the URL immediately (do NOT leave it in history)
        try {
          window.history.replaceState(null, '', '/account');
        } catch (_) { /* ignore */ }
        // Land on /account so the customer sees their dashboard
        navigate('/account', { replace: true, state: { justSignedInWithGoogle: true, needsMobile: !!result?.needs_mobile } });
        onDone?.(result);
      } catch (e) {
        const msg = e?.response?.data?.detail || e?.message || 'Google sign-in failed';
        setErr(typeof msg === 'string' ? msg : 'Google sign-in failed');
      }
    })();
  }, [location.hash, exchangeGoogleSession, navigate, onDone]);

  return (
    <div className="fixed inset-0 z-[9999] bg-white flex items-center justify-center" data-testid="google-auth-callback">
      {!err ? (
        <div className="flex flex-col items-center gap-3 text-slate-700">
          <Loader2 className="animate-spin text-emerald-600" size={32} />
          <p className="text-sm font-semibold">Finishing Google sign-in…</p>
          <p className="text-xs text-slate-500">Please wait a moment</p>
        </div>
      ) : (
        <div className="flex flex-col items-center gap-3 max-w-sm text-center">
          <AlertCircle className="text-red-600" size={32} />
          <p className="text-sm font-bold text-red-700">Sign-in failed</p>
          <p className="text-xs text-slate-600">{err}</p>
          <button
            onClick={() => { window.location.href = '/account'; }}
            className="mt-2 px-4 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold"
            data-testid="google-auth-callback-back"
          >
            Back to login
          </button>
        </div>
      )}
    </div>
  );
}
